import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react"
import { AppApi } from "services/api-routes"
import EmbedComms from "utility/EmbedComms"
import { getUserData, getToken, setLocalstorageAuthKeys, clearLocalstorageAuthKeys } from "utility/Utils"
import { estaEmFrame } from "utility/embedSession"
import { ClientContext } from "@contexts/Client"
import { AbilityContext } from '@contexts/Can'
import { getAbilities } from '@configs/acl/initialAbility'
import DEBUG from "../IsDebugOn"

const EmbedContext = createContext(null)
const FALLBACK_SEM_HANDSHAKE_MS = 10000

export const EmbedProvider = ({ children }) => {
  const [params, setParams] = useState({})
  const [authorized, setAuthorized] = useState(false)
  const [error, setError] = useState(null)
  const [autenticando, setAutenticando] = useState(false)
  const [autenticacaoTentada, setAutenticacaoTentada] = useState(false)
  const [loginPorTokenFalhou, setLoginPorTokenFalhou] = useState(false)

  const ability = useContext(AbilityContext)
  const { handleUserData, handleResetClient } = useContext(ClientContext)
  const embedCommRef = useRef(new EmbedComms())
  const autenticandoRef = useRef(false)

  const forcarAutenticado = () => {
    setAuthorized(true)
    setAutenticacaoTentada(true)
  }

  const processUserData = useCallback(async (userData) => {
    try {
      ability.update(getAbilities(userData))
      await handleUserData(userData)
      return true
    } catch (error) {
      DEBUG() && console.error("[processUserData] erro:", error)
      ability.update(getAbilities(null))
      setError("Erro ao carregar dados do usuário")
      return false
    }
  }, [handleUserData])

  const autenticarViaToken = useCallback(async (params) => {
    if (autenticandoRef.current) return false

    autenticandoRef.current = true
    setAutenticando(true)
    let autenticou = false

    try {
      const session = params?.session
      if (session?.userData && session?.accessToken) {
        setLocalstorageAuthKeys(session)
        autenticou = await processUserData(session.userData)
        if (!autenticou) clearLocalstorageAuthKeys()
        setAuthorized(autenticou)
        return autenticou
      }

      if ((!params?.apikey || params?.apikey.trim() === '') && (!params?.bearer || params?.bearer.trim() === '')) {
        return false
      }

      clearLocalstorageAuthKeys()
      const res = await AppApi.client.loginPorTokens(params.apikey, params.bearer)
      if (res?.userData && res?.accessToken) {
        setLocalstorageAuthKeys(res)
        autenticou = await processUserData(res.userData)
        if (!autenticou) clearLocalstorageAuthKeys()
        setAuthorized(autenticou)
      }
      return autenticou
    } catch (err) {
      DEBUG() && console.error("[autenticarViaToken] Erro:", err)
      clearLocalstorageAuthKeys()
      setError("Falha na autenticação")
      return false
    } finally {
      autenticandoRef.current = false
      setAutenticando(false)
      setAutenticacaoTentada(true)
      if (!autenticou) setLoginPorTokenFalhou(true)
    }
  }, [processUserData])

  const retryAuthOnce = useCallback(async () => {
    embedCommRef.current?.dispatch?.('requestParams')
    if (autenticandoRef.current) return false
    const hasParams = !!(params?.apikey || params?.bearer)
    if (!hasParams) return false
    setLoginPorTokenFalhou(false)
    return await autenticarViaToken(params)
  }, [params, autenticarViaToken])

  useEffect(() => {
    const embedComm = embedCommRef.current
    let isMounted = true

    const handleParams = async (incoming) => {
      if (!isMounted) return

      const newParams = {
        apikey: incoming?.Apikey,
        bearer: incoming?.Bearer,
        logo: incoming?.logoURI,
        session: incoming?.Session,
        guiaSelecionada: incoming?.GuiaSelecionada || null,
        pacienteSelecionado: incoming?.PacienteSelecionado || null,
        acaoSelecionada: incoming?.AcaoSelecionada || null,
        emExtensao: incoming?.EmExtensao === true
      }

      const firstTime = !params?.apikey && !params?.bearer
      const sameParams = params?.apikey === newParams?.apikey && params?.bearer === newParams?.bearer
      const alreadyTried = autenticandoRef.current || authorized

      const usuarioArmazenado = getUserData()?.id
      const usuarioRecebido = newParams?.session?.userData?.id
      const trocouDeUsuario = !!usuarioRecebido && !!usuarioArmazenado && usuarioRecebido !== usuarioArmazenado

      setParams((prev) => ({ ...prev, ...newParams }))
      if (trocouDeUsuario || ((firstTime || !sameParams) && !alreadyTried && !loginPorTokenFalhou)) {
        const success = await autenticarViaToken(newParams)
        if (!success) setError("Não foi possível autenticar via tokens")
      }
    }

    embedComm.on("params", handleParams)

    return () => {
      isMounted = false
      embedComm.off("params", handleParams)
    }
  }, [autenticarViaToken, params, authorized])

  useEffect(() => {
    if (!estaEmFrame()) {
      const existingToken = getToken()
      const existingUser = getUserData()
      if (existingToken && existingUser) setAuthorized(true)
      setAutenticacaoTentada(true)
      return
    }

    const timeout = setTimeout(() => {
      if (!autenticandoRef.current) setAutenticacaoTentada(true)
    }, FALLBACK_SEM_HANDSHAKE_MS)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    const embedComm = embedCommRef.current

    const handleLogout = () => {
      clearLocalstorageAuthKeys()
      handleResetClient()
      setAuthorized(false)
      setAutenticacaoTentada(true)
      setLoginPorTokenFalhou(false)
      setParams({})
    }

    embedComm.on("logout", handleLogout)

    return () => embedComm.off("logout", handleLogout)
  }, [handleResetClient])

  return (
    <EmbedContext.Provider value={{
      params,
      authorized,
      error,
      embedComm: embedCommRef.current,
      autenticando,
      autenticacaoTentada,
      forcarAutenticado,
      retryAuthOnce
    }}>
      {children}
    </EmbedContext.Provider>
  )
}

export const useEmbed = () => useContext(EmbedContext)
